import React from 'react';
import { Empty, Progress } from 'antd';
import MachineResetControl from './MachineResetControl';

/**
 * MachineDailyStatusCards - cards de ocupação diária por máquina.
 *
 * Renderização compartilhada entre o drawer do PWA (MachineDailyStatusButton)
 * e o Dashboard do painel admin (DRY: o mesmo visual nos dois lugares).
 * Cada card mostra:
 *  - saldo da MÁQUINA vs capacidade (barra de progresso);
 *  - nível do TANQUE vs capacidade (barra de progresso);
 *  - composição do saldo (auditoria visual) e perdas registradas hoje;
 *  - aviso de resíduo relevante do dia anterior;
 *  - botão "Zerar máquina" (quando `canReset`).
 *
 * Os cards usam flex-wrap: em telas largas (painel) formam uma grade
 * responsiva; no drawer estreito do PWA viram uma coluna única.
 *
 * Props:
 * - machines: linhas do GET /dashboard/machines-daily-balance.
 * - canReset: exibe o botão de zeramento (Admin, Operador-comprador, Líder).
 * - onReload: callback após um zeramento (recarregar o balanço).
 */
const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

// Cor da ocupação: verde (<70%), âmbar (70-90%), vermelho (>90%), cinza sem capacidade.
export const occupancyColor = (pct) => {
  if (pct == null) return '#9ca3af';
  if (pct > 90) return '#c1281a';
  if (pct >= 70) return '#b45309';
  return '#2f7d32';
};

// Resíduo do dia anterior relevante: > 5% da capacidade da máquina (ou
// > 0 quando não há capacidade cadastrada para comparar).
export const hasRelevantResidual = (m) => {
  const residual = Number(m.residualYesterday || 0);
  if (residual <= 0) return false;
  if (m.capacity_liters != null && m.capacity_liters > 0) return residual > m.capacity_liters * 0.05;
  return true;
};

const MachineDailyStatusCards = ({ machines = [], canReset = false, onReload }) => {
  if (!machines.length) return <Empty description="Nenhuma máquina ativa." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        {machines.map((m) => {
          const color = occupancyColor(m.occupancyPct);
          const tankColor = occupancyColor(m.tankOccupancyPct);
          return (
            <div
              key={m.machine_id}
              style={{
                flex: '1 1 320px',
                minWidth: 300,
                border: '1px solid #e5e7eb',
                borderLeft: `6px solid ${color}`,
                borderRadius: 12,
                padding: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                background: '#fafbff'
              }}
              aria-label={`${m.code}: saldo da máquina ${fmt(m.saldo)} litros, tanque ${fmt(m.tankLevel)} litros`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ fontSize: 22 }}>{m.code}</strong>
                  <div style={{ color: '#6b7280', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
                </div>
                <strong style={{ fontSize: 26, color, whiteSpace: 'nowrap' }}>{fmt(m.saldo)} L</strong>
              </div>

              {/* Alerta de virada de dia: a máquina fechou ontem com resíduo
                  relevante, então o "0" de hoje pode não refletir a máquina. */}
              {hasRelevantResidual(m) && (
                <div
                  role="alert"
                  style={{
                    background: '#fef3c7',
                    border: '1px solid #f59e0b',
                    borderRadius: 8,
                    padding: '8px 10px',
                    fontSize: 13,
                    color: '#92400e'
                  }}
                >
                  <strong>Aviso:</strong> ontem esta máquina fechou com <strong>{fmt(m.residualYesterday)} L</strong> de saldo.
                  O balanço de hoje começou do zero, mas a máquina física pode conter esse resíduo.
                </div>
              )}

              {/* ---- Barra da MÁQUINA ---- */}
              {m.capacity_liters != null ? (
                <div>
                  <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>Máquina</div>
                  <Progress
                    percent={m.occupancyPct == null ? 0 : Math.min(Math.max(m.occupancyPct, 0), 100)}
                    strokeColor={color}
                    size={['100%', 12]}
                    showInfo={false}
                    aria-label={`Ocupação da máquina ${m.code}: ${fmt(m.occupancyPct)} por cento`}
                  />
                  <div style={{ fontSize: 16, fontWeight: 700, color, marginTop: 2 }}>
                    {fmt(m.occupancyPct)}% de {fmt(m.capacity_liters)} L
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 14, color: '#9ca3af' }}>Capacidade da máquina não cadastrada — peça ao Admin para preencher.</div>
              )}

              {/* ---- Barra do TANQUE ---- */}
              {m.tank_capacity_liters != null ? (
                <div>
                  <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>
                    Tanque: <strong style={{ color: tankColor }}>{fmt(m.tankLevel)} L</strong>
                    {m.tankLastReadingAt && (
                      <span style={{ fontWeight: 400 }}>
                        {' '}· última leitura {new Date(m.tankLastReadingAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                    {!m.tankLastReadingAt && <span style={{ fontWeight: 400 }}> · sem leitura hoje</span>}
                  </div>
                  <Progress
                    percent={m.tankOccupancyPct == null ? 0 : Math.min(Math.max(m.tankOccupancyPct, 0), 100)}
                    strokeColor={tankColor}
                    size={['100%', 12]}
                    showInfo={false}
                    aria-label={`Ocupação do tanque de ${m.code}: ${fmt(m.tankOccupancyPct)} por cento`}
                  />
                  <div style={{ fontSize: 16, fontWeight: 700, color: tankColor, marginTop: 2 }}>
                    {fmt(m.tankOccupancyPct)}% de {fmt(m.tank_capacity_liters)} L
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 14, color: '#9ca3af' }}>
                  Tanque: <strong>{fmt(m.tankLevel)} L</strong> — capacidade do tanque não cadastrada.
                </div>
              )}

              {/* Composição do saldo da máquina (auditoria visual). */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px 16px',
                  fontSize: 16,
                  color: '#374151'
                }}
              >
                <span>RC: <strong>{fmt(m.rcAdded)} L</strong></span>
                <span>Processado: <strong>{fmt(m.processedSolvent)} L</strong></span>
                <span>Desc. RC/Borra: <strong>{fmt(m.machineDischarged)} L</strong></span>
                <span>Transferido ao tanque: <strong>{fmt(m.transferredToTank)} L</strong></span>
                {m.resets?.count > 0 && (
                  <span>Perda registrada: <strong style={{ color: '#c1281a' }}>{fmt(m.resets.lossTotal)} L</strong></span>
                )}
              </div>

              {/* Perda já registrada hoje (zeramento de turno). */}
              {m.resets?.count > 0 && (
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  Zerada hoje às {new Date(m.resets.lastAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  {' '}— perda de <strong>{fmt(m.resets.lossMachine)} L</strong> na máquina e <strong>{fmt(m.resets.lossTank)} L</strong> no tanque
                  {m.resets.count > 1 ? ` (${m.resets.count} zeramentos)` : ''}.
                </div>
              )}

              {/* Zeramento (troca de turno): registra o residual como perda.
                  Visível só para Admin, Operador-comprador e Líder. */}
              {canReset && (
                <div style={{ alignSelf: 'flex-start', marginTop: 'auto' }}>
                  <MachineResetControl machine={m} onDone={onReload} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
        Balanço do dia atual (zera à meia-noite). Máquina = RC + processado − descargas de Recarga/Borra − transferências − perdas registradas.
        Tanque = última leitura (ou zeramento) − descargas de Solvente/Água/Amarelo.
      </div>
    </div>
  );
};

export default MachineDailyStatusCards;
